import { message } from "@/atoms"
import { ExportContext } from "@/hooks/useExport"
import { useServices } from "@/hooks/useServices"
import { useTranslation } from "@/hooks/useTranslation"
import { MessageExport } from "@/molecules/MessageExport"
import { useEffect, useMemo, useRef, useState } from "react"
import { createTask, downloadTextTask } from "./tasks"

import styles from "./ExportProvider.module.scss"

import type { Analytic, SettingsExport } from "."

export const ExportProvider = ({ children }: any) => {
  const timeoutIdByURL = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {}
  )
  const [messageApi, contextHolder] = message.useMessage()
  const [tasksByUrl, setTasksByUrl] = useState<
    Record<
      string,
      { id?: string; url: string; params?: Record<string, string> }
    >
  >({})
  const { http, download, logger } = useServices()
  const { t: tExport } = useTranslation("translation", {
    keyPrefix: "general.export"
  })

  const value = useMemo(() => {
    const removeTask = (url: string, destroyMessage?: boolean) => {
      if (timeoutIdByURL.current[url]) {
        clearTimeout(timeoutIdByURL.current[url])
        delete timeoutIdByURL.current[url]
      }

      if (destroyMessage) {
        messageApi.destroy(url)
      }

      setTasksByUrl((prev) => {
        const { [url]: _, ...rest } = prev

        return rest
      })
    }

    const onEvent = (key: string, type: "success" | "error") => {
      messageApi.open({
        key,
        type,
        duration: 3,
        content:
          type === "success"
            ? tExport(type, "Export completed successfully.")
            : tExport(type, "Export failed.")
      })
      value.removeTask(key)
    }

    return {
      tasksByUrl,
      removeTask,
      addTask: (
        url: string,
        params?: Record<string, string>,
        analytic?: Analytic,
        settings?: SettingsExport
      ) => {
        const formatText = params?.format === "json" ? "JSON" : "CSV"

        messageApi.open({
          key: url,
          className: styles.messageContainer,
          duration: 0,
          type: "loading",
          content: (
            <MessageExport
              text={`Exporting ${formatText}...`}
              onCancel={() => messageApi.destroy(url)}
            />
          )
        })

        if (settings?.exportType === "downloadText")
          downloadTextTask(
            { url, params },
            {
              http,
              analytic,
              logger,
              download,
              filename: settings?.filename,
              onSuccess: (_, key: string = url) => {
                onEvent(key, "success")
              },
              onError: (key: string = url) => {
                onEvent(key, "error")
              }
            }
          )
        else
          createTask(
            { url, params },
            {
              timeoutIdByURL,
              http,
              download,
              onError: (url: string) => {
                onEvent(url, "error")
              },
              onSuccess: (url: string, key: string) => {
                download.downloadFileFromUrl(
                  { url, filename: "name" },
                  { logger }
                )

                onEvent(key, "success")
              },
              onEmail: (key: string) => {
                messageApi.open({
                  key,
                  duration: 5,
                  type: "success",
                  content:
                    "CSV export is being prepared, a link will be sent to your email shortly."
                })

                value.removeTask(key)
              },
              logger,
              analytic
            }
          )

        setTasksByUrl((prev) => {
          return {
            ...prev,
            [url]: {
              url,
              params
            }
          }
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksByUrl, messageApi])

  useEffect(() => {
    const timeoutIds = timeoutIdByURL.current

    window.onbeforeunload = function () {
      if (Object.keys(timeoutIds || {}).length) {
        return true
      }
    }

    return () => {
      Object.keys(timeoutIds).forEach((url) => {
        clearTimeout(timeoutIds[url])

        delete timeoutIds[url]
      })

      window.onbeforeunload = null
    }
  }, [])

  return (
    <ExportContext.Provider value={value}>
      {contextHolder}
      {children}
    </ExportContext.Provider>
  )
}
